import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, MessageCircle, XCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { waPackage, waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "invalid_callback") { setState("failed"); return; }
        tries.current += 1;
        if (tries.current < 12) setTimeout(poll, 2500);
        else setState("pending");
      } catch {
        if (!cancelled) setState("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const pkgName = info?.package_name || "your holiday";
  const wa = info?.package_name ? waPackage(info.package_name) : waLink("Hi D Travel Link, I just paid my booking advance. Looking forward to planning my trip!");

  return (
    <div className="py-20 lg:py-28" data-testid="payment-success-page">
      <SEO title="Booking Confirmation" />
      <div className="max-w-xl mx-auto px-4 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-skybrand mx-auto animate-spin" />
            <h1 className="mt-5 font-heading text-3xl font-extrabold text-slate-900">Confirming your payment...</h1>
            <p className="mt-2 text-slate-500 text-sm">Please wait while we verify your booking advance with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="mt-5 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Booking Advance Received!</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Your ₹{info?.amount ? Number(info.amount).toLocaleString("en-IN") : "5,000"} advance for <span className="font-semibold text-navy">{pkgName}</span> is confirmed and fully adjustable against your final quote.
              A D Travel Link expert from Thane will contact you shortly to finalise your dates, hotels and flights.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="payment-success-whatsapp"
                 className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-heading font-bold text-white hover:bg-[#1fb857] transition-colors">
                <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
              </a>
              <Link to="/" data-testid="payment-success-home"
                    className="rounded-full border border-navy px-7 py-3.5 font-heading font-bold text-navy hover:bg-navy hover:text-white transition-colors">
                Back to Home
              </Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" />
            <h1 className="mt-5 font-heading text-3xl font-extrabold text-slate-900">Payment is processing</h1>
            <p className="mt-2 text-slate-500 text-sm">Your payment is being confirmed by the bank. If you were charged, it will reflect shortly — or WhatsApp us and we will verify instantly.</p>
            <a href={waLink("Hi D Travel Link, I paid my booking advance but the status shows processing. Please verify.")} target="_blank" rel="noopener noreferrer"
               className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-heading font-bold text-white">
              <MessageCircle className="w-4 h-4" /> Verify on WhatsApp
            </a>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto" />
            <h1 className="mt-5 font-heading text-3xl font-extrabold text-slate-900">We couldn't confirm this payment</h1>
            <p className="mt-2 text-slate-500 text-sm">If any amount was debited it is auto-refunded by your bank within 5–7 days. Please try again or reach us on WhatsApp.</p>
            <Link to="/packages" className="mt-6 inline-block rounded-full bg-navy px-7 py-3.5 font-heading font-bold text-white">Browse Holidays</Link>
          </>
        )}
      </div>
    </div>
  );
}
